import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  retry,
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';

const mutex = new Mutex();

const baseQuery = (apiHost: string) => {
  let headersContent;

  if (process.env.DEV_SECRET_KEY) {
    headersContent = {
      'x-baseella-allow-secret': process.env.DEV_SECRET_KEY,
    };
  }

  const baseQueryWithRetry = retry(
    fetchBaseQuery({
      baseUrl: apiHost,
      credentials: 'include',
      headers: headersContent,
      prepareHeaders: (headers) => headers,
    }),
    { maxRetries: 0 },
  );

  return baseQueryWithRetry;
};

export default function baseQueryWithHandlingUnauthorized(apiHost: string) {
  const query: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
  > = async (args, api, extraOptions) => {
    const baseQueryWithRetry = baseQuery(apiHost);

    await mutex.waitForUnlock();

    let result = await baseQueryWithRetry(args, api, extraOptions);

    if (result.error) {
      if (result.error.status === 401) {
        if (!mutex.isLocked()) {
          const release = await mutex.acquire();

          try {
            const refreshResult = (await baseQueryWithRetry(
              {
                url: '/v1/tokens/refresh',
                method: 'POST',
              },
              api,
              extraOptions,
            )) as {
              data: {
                data: {
                  userId: string;
                };
              };
            };

            if (refreshResult.data) {
              localStorage.removeItem('userId');

              localStorage.setItem(
                'userId',
                JSON.stringify(refreshResult.data?.data),
              );

              result = await baseQueryWithRetry(args, api, extraOptions);
            } else {
              const logout = await baseQueryWithRetry(
                {
                  url: '/v1/auth/logout',
                  method: 'POST',
                },
                api,
                extraOptions,
              );

              if (logout) {
                localStorage.removeItem('userId');
                localStorage.removeItem('activePermissions');

                window.location.href = '/login';
              }
            }
          } finally {
            release();
          }
        } else {
          await mutex.waitForUnlock();

          result = await baseQueryWithRetry(args, api, extraOptions);
        }
      }
    }

    return result;
  };

  return query;
}
